import { useState } from "react";
import { Calculator as CalcIcon, Send, Clock3 } from "lucide-react";
import { Reveal, SectionHeading, waLink } from "./shared";

const DEVICES = {
  "Windows Laptop": 1.0,
  "MacBook / iMac": 1.3,
  "Desktop PC": 1.1,
  "Office / Business IT": 1.4,
};

const SERVICES = {
  "Screen Replacement": [120, 280, "Same day – 2 days"],
  "Battery Replacement": [80, 180, "Same day"],
  "SSD Upgrade + Data Clone": [98, 220, "Same day – 24h"],
  "RAM Upgrade": [60, 160, "Same day"],
  "Motherboard / Logic Board Repair": [150, 380, "2 – 4 days"],
  "Virus & Malware Removal": [50, 120, "Same day"],
  "OS Reinstall & Setup": [60, 140, "Same day – 24h"],
};

const round5 = (n) => Math.round(n / 5) * 5;

const selectCls =
  "w-full bg-void border border-slate-700 rounded-xl px-4 py-3.5 text-white text-sm focus:outline-none focus:border-brand-lime transition-colors appearance-none cursor-pointer";

export const Calculator = () => {
  const [device, setDevice] = useState("Windows Laptop");
  const [service, setService] = useState("SSD Upgrade + Data Clone");

  const [lo, hi, eta] = SERVICES[service];
  const mult = DEVICES[device];
  const estLo = round5(lo * mult);
  const estHi = round5(hi * mult);

  return (
    <section id="calculator" data-testid="calculator-section" className="relative py-24 sm:py-32 bg-navy/40 overflow-hidden">
      <div className="absolute top-0 left-1/3 w-[24rem] h-[24rem] rounded-full bg-brand-lime/8 blur-[130px] pointer-events-none" />
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <SectionHeading
          eyebrow="Transparent Pricing"
          title="Instant repair"
          accent="estimate."
          sub="No hidden surcharges. Pick your device and service — send the quote to AFZ with one tap."
          align="center"
        />
        <Reveal delay={0.15}>
          <div className="mt-12 bg-card/90 border border-slate-800 rounded-2xl p-6 sm:p-10 clip-corner">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Your Device</label>
                <select
                  data-testid="calculator-device-select"
                  value={device}
                  onChange={(e) => setDevice(e.target.value)}
                  className={selectCls}
                >
                  {Object.keys(DEVICES).map((d) => (
                    <option key={d}>{d}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Service Needed</label>
                <select
                  data-testid="calculator-service-select"
                  value={service}
                  onChange={(e) => setService(e.target.value)}
                  className={selectCls}
                >
                  {Object.keys(SERVICES).map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-6 bg-void border border-brand-lime/30 rounded-xl px-6 py-6">
              <div className="text-center sm:text-left">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Estimated Range</p>
                <p data-testid="calculator-estimate" className="font-display text-4xl sm:text-5xl font-black text-brand-lime text-glow-lime mt-1">
                  S${estLo} – S${estHi}
                </p>
                <p className="mt-2 flex items-center justify-center sm:justify-start gap-2 text-sm text-slate-400">
                  <Clock3 className="w-4 h-4 text-brand-cyan" /> {eta}
                </p>
              </div>
              <a
                data-testid="calculator-send-quote-button"
                href={waLink(`Hi AFZ Tech Solution, estimate request — Device: ${device}, Service: ${service} (site estimate S$${estLo}–S$${estHi}). Please confirm a quote.`)}
                target="_blank"
                rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-2 bg-brand-lime text-void font-display font-bold uppercase px-7 py-4 rounded-full hover:bg-brand-limedark hover:scale-[1.03] transition-all duration-300 shadow-[0_0_24px_rgba(170,255,0,0.35)]"
              >
                <Send className="w-4 h-4" /> Send Quote to AFZ
              </a>
            </div>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
              <CalcIcon className="w-3 h-3 inline mr-1" /> Final quote confirmed after free diagnostic
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
